import { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import "./App.css";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminProtectedRoute from "./components/AdminProtectedRoute";
import { Toaster } from "@/components/ui/sonner";
import { SkipToContent } from "@/components/a11y/SkipToContent";
import NetworkStatusBanner from "@/components/feedback/NetworkStatusBanner";
import ErrorBoundary from "@/components/feedback/ErrorBoundary";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { useAuth } from "@/hooks/useAuth";
import DashboardShell from "@/components/DashboardShell";
import DevDebugPanel from "@/components/DevDebugPanel";
import NotFoundPage from "./pages/NotFoundPage";

const LandingPage = lazy(() => import("./pages/LandingPage"));
const BrowsePage = lazy(() => import("./pages/BrowsePage"));
const BrowseOrInvitePage = lazy(() => import("./pages/BrowseOrInvitePage"));
const AddBusinessManualPage = lazy(
  () => import("./pages/AddBusinessManualPage"),
);
const BusinessDetailPage = lazy(() => import("./pages/BusinessDetailPage"));
const BusinessDashboard = lazy(() => import("./pages/BusinessDashboard"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const RoleSelectionPage = lazy(() => import("./pages/RoleSelectionPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const AuthCallbackPage = lazy(() => import("./pages/AuthCallbackPage"));
const AdvocateDashboard = lazy(() => import("./pages/AdvocateDashboard"));
const MyCardEditorPage = lazy(() => import("./pages/MyCardEditorPage"));
const BusinessCardEditorPage = lazy(
  () => import("./pages/BusinessCardEditorPage"),
);
const GiftCardWalletPage = lazy(() => import("./pages/GiftCardWalletPage"));
const PartnerRecommendationsPage = lazy(
  () => import("./pages/PartnerRecommendationsPage"),
);
const VoucheePulsePage = lazy(() => import("./pages/VoucheePulsePage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const BusinessProfileSetup = lazy(() => import("./pages/BusinessProfileSetup"));
const BusinessInvoicing = lazy(() => import("./pages/BusinessInvoicingPage"));
const ProposalsPage = lazy(() => import("./pages/ProposalsPage"));
const ServiceCatalogPage = lazy(() => import("./pages/ServiceCatalogPage"));
const EarningsDashboard = lazy(() => import("./pages/EarningsDashboard"));
const BusinessPartnershipsPage = lazy(
  () => import("./pages/BusinessPartnershipsPage"),
);
const MyPartnershipsPage = lazy(() => import("./pages/MyPartnershipsPage"));
const YourVoucheesPage = lazy(() =>
  import("./pages/YourVoucheesPage").then((module) => ({
    default: module.YourVoucheesPage,
  })),
);
const MyVouchesPage = lazy(() =>
  import("./pages/MyVouchesPage").then((module) => ({
    default: module.MyVouchesPage,
  })),
);
const PartnerInviteAcceptPage = lazy(
  () => import("./pages/PartnerInviteAcceptPage"),
);
const PartnershipDiscoveryPage = lazy(
  () => import("./pages/PartnershipDiscoveryPage"),
);
const PartnershipManagementPage = lazy(
  () => import("./pages/PartnershipManagementPage"),
);
const BusinessRolodexPage = lazy(() => import("./pages/BusinessRolodexPage"));
const BusinessContactsPage = lazy(() => import("./pages/BusinessContactsPage"));
const PersonalVoucheePage = lazy(() => import("./pages/PersonalVoucheePage"));
const SingleVouchSharePage = lazy(() => import("./pages/SingleVouchSharePage"));
const PublicVoucheePage = lazy(() => import("./pages/PublicVoucheePage"));
const ReferralRedirect = lazy(() => import("./pages/ReferralRedirect"));
const AdminLoginPage = lazy(() => import("./pages/AdminLoginPage"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const AdminSubscriptionPlansPage = lazy(
  () => import("./pages/AdminSubscriptionPlansPage"),
);
const AdminPromoCodesPage = lazy(() => import("./pages/AdminPromoCodesPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const BusinessSettingsPage = lazy(() => import("./pages/BusinessSettingsPage"));
const PaymentSettingsPage = lazy(() => import("./pages/PaymentSettingsPage"));
const PlatformRulesPage = lazy(() => import("./pages/PlatformRulesPage"));
const BuyGiftCardPage = lazy(() => import("./pages/BuyGiftCardPage"));
const PublicBusinessProfilePage = lazy(
  () => import("./pages/PublicBusinessProfilePage"),
);
const TermsConditionsPage = lazy(() => import("./pages/TermsConditionsPage"));
const UpgradeToVoucheePage = lazy(() => import("./pages/UpgradeToVoucheePage"));
// Tax reporting removed - not needed
const GoogleBusinessProfilePage = lazy(
  () => import("./pages/GoogleBusinessProfilePage"),
);
const DisputeResolutionPage = lazy(
  () => import("./pages/DisputeResolutionPage"),
);
const ManualReviewDashboard = lazy(
  () => import("./pages/ManualReviewDashboard"),
);
const PlatformFeesPage = lazy(() => import("./pages/PlatformFeesPage"));
const CustomerManagementPage = lazy(
  () => import("./pages/CustomerManagementPage"),
);
const CustomerDetailPage = lazy(() => import("./pages/CustomerDetailPage"));
const CustomerJourneyDashboard = lazy(
  () => import("./pages/CustomerJourneyDashboard"),
);
const InviteBusinessPage = lazy(() => import("./pages/InviteBusinessPage"));
const AccountSuspensionPage = lazy(
  () => import("./pages/AccountSuspensionPage"),
);
const IndustrySelectionPage = lazy(
  () => import("./pages/signup/IndustrySelectionPage"),
);
const BusinessTypePage = lazy(() => import("./pages/signup/BusinessTypePage"));
const BusinessSearchPage = lazy(
  () => import("./pages/signup/BusinessSearchPage"),
);
const CommissionTemplatePage = lazy(
  () => import("./pages/signup/CommissionTemplatePage"),
);
const BusinessBasicsPage = lazy(
  () => import("./pages/signup/BusinessBasicsPage"),
);
const StripeConnectOnboarding = lazy(
  () => import("./pages/StripeConnectOnboarding"),
);
const PaymentPage = lazy(() => import("./pages/PaymentPage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const LeadCapturePage = lazy(() => import("./pages/LeadCapturePage"));
const QuickBooksConnect = lazy(() => import("./pages/QuickBooksConnect"));
const RecordSalePage = lazy(() => import("./pages/RecordSalePage"));
const SmartListsPage = lazy(() => import("./pages/SmartListsPage"));
const SmartListDetailPage = lazy(() => import("./pages/SmartListDetailPage"));
const BulkImportPage = lazy(() => import("./pages/BulkImportPage"));
const CSVImportPage = lazy(() => import("./pages/CSVImportPage"));
const ClientOutreachPage = lazy(() => import("./pages/ClientOutreachPage"));
const ReactivationCampaignPage = lazy(() => import("./pages/ReactivationCampaignPage"));
const CRMIntegrationPage = lazy(() => import("./pages/CRMIntegrationPage"));
const BusinessInvitePage = lazy(() => import("./pages/BusinessInvitePage"));
const CustomerInvitePage = lazy(() => import("./pages/CustomerInvitePage"));
const VoucheeInvitePage = lazy(() => import("./pages/VoucheeInvitePage"));
const BusinessAnalyticsPage = lazy(
  () => import("./pages/BusinessAnalyticsPage"),
);
const BusinessTransactionsPage = lazy(
  () => import("./pages/BusinessTransactionsPage"),
);
const CommissionSettingsPage = lazy(
  () => import("./pages/CommissionSettingsPage"),
);
const BusinessPayoutsPage = lazy(() => import("./pages/BusinessPayoutsPage"));
const RecruitVoucheesPage = lazy(() => import("./pages/RecruitVoucheesPage"));
const DiscountCodesPage = lazy(() => import("./pages/DiscountCodesPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminFraudAlertsPage = lazy(() => import("./pages/AdminFraudAlertsPage"));
const VerifyBusinessPage = lazy(() => import("./pages/VerifyBusinessPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const ResendVerificationPage = lazy(
  () => import("./pages/ResendVerificationPage"),
);
const ReferralClickRedirect = lazy(
  () => import("./pages/ReferralClickRedirect"),
);
const RatingsPage = lazy(() => import("./pages/RatingsPage"));
const DashboardFeatureComingSoonPage = lazy(
  () => import("./pages/DashboardFeatureComingSoonPage"),
);
const TrustScorePage = lazy(() => import("./pages/TrustScorePage"));
const AffiliateProgramsPage = lazy(() => import("./pages/AffiliateProgramsPage"));
const PerformanceRewardsPage = lazy(() => import("./pages/PerformanceRewardsPage"));
const MyInvitesPage = lazy(() => import("./pages/MyInvitesPage"));
const ShareAnalyticsPage = lazy(() => import("./pages/ShareAnalyticsPage"));
const PendingLeadsPage = lazy(() => import("./pages/PendingLeadsPage"));
const QuotesPage = lazy(() => import("./pages/QuotesPage"));
const AuditLogsPage = lazy(() => import("./pages/AuditLogsPage"));
const DataPrivacyPage = lazy(() => import("./pages/DataPrivacyPage"));
const BusinessVerificationPage = lazy(() => import("./pages/BusinessVerificationPage"));
const DirectLinksPage = lazy(() => import("./pages/DirectLinksPage"));
const CreativesPage = lazy(() => import("./pages/CreativesPage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const InvoicingViralLoopPage = lazy(() => import("./pages/InvoicingViralLoopPage"));
const SuspiciousVoucheesPage = lazy(() => import("./pages/SuspiciousVoucheesPage"));
const ClaimBusinessPage = lazy(() => import("./pages/ClaimBusinessPage"));

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <LoadingSpinner label="Loading experience..." size="lg" />
  </div>
);

function CustomerJourneyRoute() {
  const { user } = useAuth();
  return <CustomerJourneyDashboard voucheeId={user?.id} />;
}

function ManualReviewRoute() {
  const { user } = useAuth();
  const businessId =
    user?.business_id || user?.businessId || user?.business?.id || null;
  return <ManualReviewDashboard businessId={businessId} />;
}

function normalizeSignupRole(rawRole) {
  if (!rawRole) return null;

  const normalized = rawRole.trim().toLowerCase();

  if (normalized === "vouchee" || normalized === "user") {
    return "user";
  }

  if (normalized === "business") {
    return "business";
  }

  return null;
}

function SignupRoute() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const role = normalizeSignupRole(searchParams.get("role"));

  if (!role) {
    return <Navigate to={`/get-started${location.search}`} replace />;
  }

  return <SignupPage initialRole={role} />;
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <SkipToContent />
        <NetworkStatusBanner />
        <ErrorBoundary
          onError={(error, info) => {
            console.error("[App] Route rendering error:", error, info);
          }}
        >
          <div
            id="main-content"
            role="main"
            className="bg-gray-50 min-h-screen text-gray-900"
          >
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                <Route path="/" element={<LandingPage />} />
                <Route
                  path="/join/:token"
                  element={<PartnerInviteAcceptPage />}
                />
                <Route
                  path="/partner-invite/:token"
                  element={<PartnerInviteAcceptPage />}
                />
                <Route path="/join" element={<BusinessInvitePage />} />
                <Route
                  path="/invite/customer/:token"
                  element={<CustomerInvitePage />}
                />
                <Route
                  path="/invite/business/:token"
                  element={<BusinessInvitePage />}
                />
                <Route
                  path="/invite/vouchee/:token"
                  element={<VoucheeInvitePage />}
                />
                <Route
                  path="/invite-business"
                  element={<InviteBusinessPage />}
                />
                <Route path="/browse" element={<BrowsePage />} />
                <Route path="/claim/:token" element={<ClaimBusinessPage />} />
                <Route path="/browse-invite" element={<BrowseOrInvitePage />} />
                <Route
                  path="/browse-or-invite"
                  element={<BrowseOrInvitePage />}
                />
                <Route
                  path="/add-business-manual"
                  element={<AddBusinessManualPage />}
                />
                <Route path="/pulse" element={<VoucheePulsePage />} />
                {/* Public business profile should be the canonical /business/:slug route */}
                <Route
                  path="/business/:slug"
                  element={<PublicBusinessProfilePage />}
                />
                <Route
                  path="/business/:id/details"
                  element={<BusinessDetailPage />}
                />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/get-started" element={<RoleSelectionPage />} />
                <Route path="/signup" element={<SignupRoute />} />
                <Route
                  path="/forgot-password"
                  element={<ForgotPasswordPage />}
                />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route
                  path="/verify-business"
                  element={<VerifyBusinessPage />}
                />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route
                  path="/resend-verification"
                  element={<ResendVerificationPage />}
                />
                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route
                  path="/admin"
                  element={
                    <AdminProtectedRoute>
                      <DashboardShell>
                        <AdminDashboard />
                      </DashboardShell>
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/settings"
                  element={
                    <AdminProtectedRoute>
                      <DashboardShell>
                        <AdminSettings />
                      </DashboardShell>
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/settings/subscription-plans"
                  element={
                    <AdminProtectedRoute>
                      <DashboardShell>
                        <AdminSubscriptionPlansPage />
                      </DashboardShell>
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/settings/promo-codes"
                  element={
                    <AdminProtectedRoute>
                      <DashboardShell>
                        <AdminPromoCodesPage />
                      </DashboardShell>
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/bulk-import"
                  element={
                    <AdminProtectedRoute>
                      <DashboardShell>
                        <BulkImportPage />
                      </DashboardShell>
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/rules"
                  element={
                    <AdminProtectedRoute>
                      <DashboardShell>
                        <PlatformRulesPage />
                      </DashboardShell>
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/disputes"
                  element={
                    <AdminProtectedRoute>
                      <DashboardShell>
                        <DisputeResolutionPage />
                      </DashboardShell>
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/manual-review"
                  element={
                    <AdminProtectedRoute>
                      <DashboardShell>
                        <ManualReviewDashboard />
                      </DashboardShell>
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/fraud-alerts"
                  element={
                    <AdminProtectedRoute>
                      <DashboardShell>
                        <AdminFraudAlertsPage />
                      </DashboardShell>
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/platform-fees"
                  element={
                    <AdminProtectedRoute>
                      <DashboardShell>
                        <PlatformFeesPage />
                      </DashboardShell>
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/customers"
                  element={
                    <AdminProtectedRoute>
                      <DashboardShell>
                        <CustomerManagementPage />
                      </DashboardShell>
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/customer-journey"
                  element={
                    <AdminProtectedRoute>
                      <DashboardShell>
                        <CustomerJourneyDashboard />
                      </DashboardShell>
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/account-suspension"
                  element={
                    <ProtectedRoute>
                      <DashboardShell>
                        <AccountSuspensionPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route path="/terms" element={<TermsOfServicePage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/platform-rules" element={<PlatformRulesPage />} />
                <Route
                  path="/terms-conditions"
                  element={<TermsConditionsPage />}
                />
                <Route path="/buy-gift-card" element={<BuyGiftCardPage />} />
                <Route
                  path="/business/:businessId/public"
                  element={<PublicBusinessProfilePage />}
                />
                {/* Archived feature route */}
                <Route
                  path="/financing"
                  element={<Navigate to="/business-dashboard" replace />}
                />
                <Route
                  path="/upgrade"
                  element={
                    <ProtectedRoute>
                      <DashboardShell>
                        <UpgradeToVoucheePage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/signup/business"
                  element={<SignupPage initialRole="business" />}
                />
                <Route
                  path="/signup/business/industry"
                  element={<IndustrySelectionPage />}
                />
                <Route
                  path="/signup/business/type"
                  element={<BusinessTypePage />}
                />
                <Route
                  path="/signup/business/search"
                  element={<BusinessSearchPage />}
                />
                <Route
                  path="/signup/business/commission-templates"
                  element={<CommissionTemplatePage />}
                />
                <Route
                  path="/signup/business/commission"
                  element={<CommissionTemplatePage />}
                />
                <Route
                  path="/signup/business/basics"
                  element={<BusinessBasicsPage />}
                />
                <Route
                  path="/stripe-connect"
                  element={
                    <ProtectedRoute requireRole="business">
                      <StripeConnectOnboarding />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/onboarding/stripe"
                  element={
                    <ProtectedRoute requireRole="business">
                      <StripeConnectOnboarding />
                    </ProtectedRoute>
                  }
                />
                <Route path="/payment" element={<PaymentPage />} />
                <Route
                  path="/payment-success"
                  element={<PaymentSuccessPage />}
                />
                <Route
                  path="/payment-settings"
                  element={
                    <ProtectedRoute>
                      <DashboardShell>
                        <PaymentSettingsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/payment-settings"
                  element={
                    <ProtectedRoute>
                      <DashboardShell>
                        <PaymentSettingsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route path="/lead-capture" element={<LeadCapturePage />} />
                <Route
                  path="/quickbooks/connect"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <QuickBooksConnect />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/quickbooks/connect"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <QuickBooksConnect />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/record-sale"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <RecordSalePage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/record-sale"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <RecordSalePage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                {/* Archived feature route */}
                <Route
                  path="/reactivation-campaigns"
                  element={<Navigate to="/client-outreach" replace />}
                />
                <Route
                  path="/dashboard/smart-lists"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <SmartListsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/smart-lists/:listId"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <SmartListDetailPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/bulk-import"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <BulkImportPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client-outreach"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <ClientOutreachPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reactivation-campaign"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <ReactivationCampaignPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/crm-integrations"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <CRMIntegrationPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/crm"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <CRMIntegrationPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <DashboardShell>
                        <AdvocateDashboard />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/my-card"
                  element={
                    <ProtectedRoute>
                      <DashboardShell>
                        <MyCardEditorPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/gift-cards"
                  element={
                    <ProtectedRoute>
                      <DashboardShell>
                        <GiftCardWalletPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/wallet"
                  element={
                    <ProtectedRoute>
                      <DashboardShell>
                        <GiftCardWalletPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/partner-recommendations"
                  element={
                    <ProtectedRoute>
                      <DashboardShell>
                        <PartnerRecommendationsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/business-profile-setup"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <BusinessProfileSetup />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/customers"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <CustomerManagementPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/customers/:customerId"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <CustomerDetailPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/business-dashboard/invite-customers"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <CustomerManagementPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                {/* Archived feature route */}
                <Route
                  path="/pos-integration"
                  element={<Navigate to="/dashboard/crm" replace />}
                />
                <Route
                  path="/quickbooks-integration"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <QuickBooksConnect />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/gbp"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <GoogleBusinessProfilePage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/manual-review"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <ManualReviewRoute />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/business-dashboard"
                  element={
                    <ProtectedRoute requireRole="business">
                      <BusinessDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/business-dashboard/integrations"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <CRMIntegrationPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/business-dashboard/invite-customers"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <CustomerManagementPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/business-dashboard/import-customers"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <CSVImportPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/business-dashboard/my-card"
                  element={
                    <ProtectedRoute requireRole="business">
                      <BusinessCardEditorPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/business-dashboard/rewards"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <PerformanceRewardsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/my-business-card"
                  element={
                    <ProtectedRoute requireRole="business">
                      <BusinessCardEditorPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/business-settings"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <BusinessSettingsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/business-invoicing"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <BusinessInvoicing />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/invoicing"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <BusinessInvoicing />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/proposals"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <ProposalsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/service-catalog"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <ServiceCatalogPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/services"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <ServiceCatalogPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/earnings"
                  element={
                    <ProtectedRoute>
                      <DashboardShell>
                        <EarningsDashboard />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/customer-journey"
                  element={
                    <ProtectedRoute>
                      <DashboardShell>
                        <CustomerJourneyRoute />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/your-vouchees"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <YourVoucheesPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/my-vouchees"
                  element={
                    <ProtectedRoute requireRole="business">
                      <YourVoucheesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/my-vouches"
                  element={
                    <ProtectedRoute>
                      <DashboardShell>
                        <MyVouchesPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/partnership-discovery"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <PartnershipDiscoveryPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/partnerships/discover"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <PartnershipDiscoveryPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/partnerships"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <PartnershipManagementPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/partnerships/manage"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <PartnershipManagementPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/partnerships"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <BusinessPartnershipsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/my-partnerships"
                  element={
                    <ProtectedRoute>
                      <DashboardShell>
                        <MyPartnershipsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/recruit"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <RecruitVoucheesPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/my-partnerships"
                  element={
                    <ProtectedRoute>
                      <DashboardShell>
                        <MyPartnershipsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/business-analytics"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <BusinessAnalyticsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/analytics"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <BusinessAnalyticsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/business-transactions"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <BusinessTransactionsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/transactions"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <BusinessTransactionsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/business-rolodex"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <BusinessRolodexPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/rolodex"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <BusinessRolodexPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/my-rolodex"
                  element={
                    <ProtectedRoute requireRole="business">
                      <Navigate to="/dashboard/rolodex" replace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/business-contacts"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <BusinessContactsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/contacts"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <BusinessContactsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/discount-codes"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <DiscountCodesPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/settings"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <BusinessSettingsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/business-settings"
                  element={
                    <ProtectedRoute requireRole="business">
                      <Navigate to="/dashboard/settings" replace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/commission-settings"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <CommissionSettingsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/payouts"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <BusinessPayoutsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                {/* Tax reporting route removed - not needed */}
                {/* Archived feature route */}
                <Route
                  path="/dashboard/pos"
                  element={<Navigate to="/dashboard/crm" replace />}
                />
                <Route
                  path="/dashboard/pos-integration"
                  element={<Navigate to="/dashboard/crm" replace />}
                />
                <Route
                  path="/dashboard/quickbooks"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <QuickBooksConnect />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/gbp"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <GoogleBusinessProfilePage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/google-business"
                  element={
                    <ProtectedRoute requireRole="business">
                      <Navigate to="/dashboard/gbp" replace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/service-catalog"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <ServiceCatalogPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/stripe"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <StripeConnectOnboarding />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/disputes"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <DisputeResolutionPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/profile"
                  element={
                    <ProtectedRoute>
                      <DashboardShell>
                        <ProfilePage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/notifications"
                  element={
                    <ProtectedRoute>
                      <DashboardShell>
                        <NotificationsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/ratings"
                  element={
                    <ProtectedRoute>
                      <DashboardShell>
                        <RatingsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/affiliate-programs"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <AffiliateProgramsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/performance-rewards"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <PerformanceRewardsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/my-invites"
                  element={
                    <ProtectedRoute>
                      <DashboardShell>
                        <MyInvitesPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/share-analytics"
                  element={
                    <ProtectedRoute>
                      <DashboardShell>
                        <ShareAnalyticsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/pending-leads"
                  element={
                    <ProtectedRoute>
                      <DashboardShell>
                        <PendingLeadsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/data-privacy"
                  element={
                    <ProtectedRoute>
                      <DashboardShell>
                        <DataPrivacyPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/quotes"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <QuotesPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/business-verification"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <BusinessVerificationPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/audit-logs"
                  element={
                    <ProtectedRoute requireRole="admin">
                      <DashboardShell>
                        <AuditLogsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/direct-links"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <DirectLinksPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/creatives"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <CreativesPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/reports"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <ReportsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/invoicing-viral"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <InvoicingViralLoopPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/suspicious-vouchees"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <SuspiciousVoucheesPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/:feature"
                  element={
                    <ProtectedRoute requireRole="business">
                      <DashboardShell>
                        <DashboardFeatureComingSoonPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/trust-score"
                  element={
                    <ProtectedRoute>
                      <DashboardShell>
                        <TrustScorePage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <DashboardShell>
                        <ProfilePage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <DashboardShell>
                        <SettingsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/notifications"
                  element={
                    <ProtectedRoute>
                      <DashboardShell>
                        <NotificationsPage />
                      </DashboardShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/commission-settings"
                  element={
                    <Navigate to="/dashboard/commission-settings" replace />
                  }
                />
                <Route
                  path="/invoicing"
                  element={<Navigate to="/dashboard/invoicing" replace />}
                />
                <Route
                  path="/recruit-vouchees"
                  element={<Navigate to="/dashboard/recruit" replace />}
                />
                <Route
                  path="/stripe-connect-onboarding"
                  element={<Navigate to="/dashboard/stripe" replace />}
                />
                <Route
                  path="/upgrade-to-vouchee"
                  element={<Navigate to="/upgrade" replace />}
                />
                <Route
                  path="/v/:secureId"
                  element={<ReferralClickRedirect />}
                />
                <Route path="/r/:code" element={<ReferralRedirect />} />
                <Route
                  path="/vouchee/:username"
                  element={<PublicVoucheePage />}
                />
                <Route
                  path="/share/:username/:business_id"
                  element={<SingleVouchSharePage />}
                />
                <Route path="/:username" element={<PersonalVoucheePage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </div>
        </ErrorBoundary>
        <CookieConsentBanner />
        <DevDebugPanel />
        <Toaster position="top-right" richColors closeButton duration={4000} />
      </AuthProvider>
    </Router>
  );
}

export default App;
